import {
  RECOMMENDATIONS_PAGE_TYPE_MAP,
  RECOMMENDATION_MPV_CONFIG
} from '../../constants/recommendationsExperiment'
import {
  RELATED_PRODUCTS_FREQUENTLY_ORDERED_AUTOMATION,
  RELATED_PRODUCTS_FREQUENTLY_ORDERED_AUTOMATION_VARIATION
} from '../../constants/supportedExperiments'
import {
  TemplateConfiguration,
  RelatedProductsType,
  FrequentlyOrderedType
} from '../../types/TemplateConfiguration'
import { formatLocale, getRegionForLocale } from '../../utils/buildHelpers'

export const isMcidSupportedForRecommendation = (
  merchandisingCategoryId: string,
  locale: string
): boolean => {
  const region = getRegionForLocale(locale)
  const formattedLocale = formatLocale(locale, true)
  const regionWildcard = `${region.toUpperCase()}-*`
  const recommendationConfig =
    RECOMMENDATION_MPV_CONFIG[formattedLocale] || RECOMMENDATION_MPV_CONFIG[regionWildcard]

  return recommendationConfig ? merchandisingCategoryId in recommendationConfig : false
}

export const addRecommendationStack = (
  templateConfiguration: TemplateConfiguration,
  merchandisingCategoryId: string,
  locale: string,
  experimentFlags?: { [key: string]: string | undefined }
): TemplateConfiguration => {
  const isRecommendationExperimentEnabled =
    experimentFlags?.[RELATED_PRODUCTS_FREQUENTLY_ORDERED_AUTOMATION] ===
    RELATED_PRODUCTS_FREQUENTLY_ORDERED_AUTOMATION_VARIATION

  const isSupportedMCID = isMcidSupportedForRecommendation(merchandisingCategoryId, locale)

  if (!isRecommendationExperimentEnabled || !isSupportedMCID) {
    return templateConfiguration
  }

  const updatedTemplateConfiguration: TemplateConfiguration = JSON.parse(
    JSON.stringify(templateConfiguration)
  )

  const relatedProductsSection: RelatedProductsType = {
    type: 'related products',
    enabled: true,
    sectionId: 'related-products-section-id'
  }

  const frequentlyOrderedSection: FrequentlyOrderedType = {
    type: 'frequently ordered',
    enabled: true,
    sectionId: 'frequently-ordered-section-id'
  }

  const sections = updatedTemplateConfiguration.sections

  const seoSectionIndex = sections.findIndex((section) => section.type === 'seo')

  if (seoSectionIndex === -1) {
    return updatedTemplateConfiguration
  }

  let insertionIndex: number
  let deleteCount: number

  if (seoSectionIndex === 0) {
    // SEO is first, insert before it without deleting anything
    insertionIndex = 0
    deleteCount = 0
  } else {
    // SEO is not first, replace the section before it
    insertionIndex = seoSectionIndex - 1
    deleteCount = 1
  }

  sections.splice(insertionIndex, deleteCount, relatedProductsSection, frequentlyOrderedSection)
  return updatedTemplateConfiguration
}

export const getRecommendationMpvId = ({
  merchandisingCategoryId,
  locale,
  type
}: {
  merchandisingCategoryId: string
  locale: string
  type: string
}): string | null => {
  const region = getRegionForLocale(locale)
  const regionWildcard = `${region.toUpperCase()}-*`

  const recommendationConfig = (RECOMMENDATION_MPV_CONFIG[formatLocale(locale, true)] ||
    RECOMMENDATION_MPV_CONFIG[regionWildcard])?.[merchandisingCategoryId]

  if (type === 'frequently ordered') {
    return recommendationConfig?.mainMpv || null
  } else if (type === 'related products') {
    return recommendationConfig?.alternativeMpv || null
  }
  return null
}

export const getRecommendationsPageType = (type: string): string | null => {
  return RECOMMENDATIONS_PAGE_TYPE_MAP[type] || null
}
